import { List, ListItemButton, ListItemText, Typography, useTheme, Box, Paper, Divider } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const ContextMenu = ({ x, y, visible, onClose, options, selectedItems }) => {
  const { t } = useTranslation();
  const menuRef = useRef();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [coords, setCoords] = useState({ top: y, left: x });

  useEffect(() => {
    if (visible && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const nextTop = Math.min(y, window.innerHeight - rect.height - 12);
      const nextLeft = Math.min(x, window.innerWidth - rect.width - 12);
      setCoords({ top: Math.max(12, nextTop), left: Math.max(12, nextLeft) });
    }
  }, [x, y, visible]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <Paper
      ref={menuRef}
      elevation={10}
      sx={{
        position: "fixed",
        zIndex: 10000, 
        top: coords.top,
        left: coords.left, 
        minWidth: 260,
        // Reverting to a clean, premium white/slate background as requested
        backgroundColor: isDark ? "#18181b" : "#ffffff", 
        border: "1px solid",
        borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: isDark 
          ? "0px 12px 48px rgba(0,0,0,0.8)" 
          : "0px 12px 32px rgba(0,0,0,0.1)",
        animation: "slideIn 0.1s ease-out",
        "@keyframes slideIn": {
          from: { opacity: 0, transform: "scale(0.98)" },
          to: { opacity: 1, transform: "scale(1)" },
        },
      }}
    >
      <List sx={{ padding: "6px" }}>
        {options.map((o, i) => {
          if (o.type === 'divider') return <Divider key={i} sx={{ my: 0.8, opacity: 0.5 }} />;
          
          const isDisabled = o.needSelectedItems && selectedItems.length === 0;
          
          return (
            <ListItemButton
              key={i}
              disabled={isDisabled}
              onClick={() => {
                if (o.needSelectedItems) {
                  o.action(selectedItems);
                } else {
                  o.action();
                }
                onClose();
              }}
              sx={{
                paddingY: "10px",
                paddingX: "16px",
                borderRadius: "6px",
                margin: "1px 0",
                display: "flex",
                alignItems: "center",
                gap: "14px",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#f0fdf4", 
                  "& .menu-text": {
                    color: isDark ? "#4ade80" : "#166534",
                  },
                  "& .menu-icon": {
                    color: isDark ? "#4ade80" : "#166534",
                  }
                },
                "&.Mui-disabled": {
                  opacity: 0.35,
                  "& .menu-text": {
                    color: isDark ? "rgba(255,255,255,0.4)" : "rgba(15,23,42,0.42)",
                  },
                  "& .menu-icon": {
                    color: isDark ? "rgba(255,255,255,0.35)" : "rgba(15,23,42,0.35)",
                  }
                }
              }}
            >
              {o.icon && (
                <Box className="menu-icon" sx={{ 
                  color: isDark ? "#d4d4d8" : "#334155",
                  display: "flex",
                  fontSize: "18px",
                  transition: "color 0.2s"
                }}>
                  {o.icon}
                </Box>
              )}
              <ListItemText sx={{ m: 0 }}>
                <Typography className="menu-text" sx={{
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: isDark ? "#f4f4f5" : "#1e293b",
                  textAlign: "left",
                  transition: "color 0.2s"
                }}>
                  {t(o.label)}
                </Typography>
              </ListItemText>
            </ListItemButton>
          );
        })}
      </List>
    </Paper>
  );
};

export default ContextMenu;
